import { logger } from '@springtree/eva-sdk-core-logger';
import { createServiceDefinition, Core } from '@springtree/eva-services-core';
import { EvaService } from './service.js';
/**
 * Provides bootstrap logic for an EVA endpoint.
 * We need to bootstrap to get access to applications and configuration
 * This provides us with details such as the default token, base urls, etc.
 * We can bootstrap both cloud and on-premise EVA endpoints the same way
 *
 * @export
 * @class EvaEndpoint
 */
export class EvaEndpoint {
    /**
     * The endpoint URL for the EVA backend
     *
     * @type {string}
     */
    endPointUri;
    /**
     * The default configuration for the EVA endpoint
     *
     * @private
     * @type {EVA.Core.GetApplicationConfigurationResponse}
     */
    configuration;
    /**
     * Unix timestamp in milliseconds when the bootstrap was done
     *
     * @private
     * @type {number}
     */
    bootstrappedAt = 0;
    /**
     * Getter for boolean state indicating the endpoint has been bootstrapped
     *
     * @readonly
     * @type {boolean}
     */
    get hasBootstrapped() {
        return !!this.configuration;
    }
    /**
     * Creates an instance of EvaEndpoint
     * Can either be a simple endpoint or serialised data
     *
     * @param {string} endPointUri The uri for the EVA endpoint to bootstrap
     */
    constructor(endPointUriOrSerialisedData) {
        if (typeof endPointUriOrSerialisedData === 'string') {
            this.endPointUri = endPointUriOrSerialisedData;
        }
        else {
            const data = endPointUriOrSerialisedData;
            this.endPointUri = data.endPointUri;
            this.configuration = data.configuration;
            this.bootstrappedAt = data.bootstrappedAt;
        }
    }
    /**
     * Create a data object representation of the EVA Endpoint instance
     *
     * @returns {IEvaEndpointSerializedData}
     */
    serialise() {
        const data = {
            endPointUri: this.endPointUri,
            configuration: this.configuration,
            bootstrappedAt: this.bootstrappedAt,
        };
        return data;
    }
    /**
     * Perform the bootstrap calls and setup current application
     */
    async bootstrap(params) {
        logger.debug(`[EVA:ENDPOINT] Bootstrapping ${this.endPointUri}...`)();
        // We will disable the EVA service interceptors to not hit on premise or
        // other behaviour changing logic. This logic should not work before the
        // bootstrap anyway
        //
        // Fetch application configuration
        //
        const getApplicationConfiguration = new EvaService(createServiceDefinition(Core.GetApplicationConfiguration), this, { disableInterceptors: true, disableEndpointBootstrapCheck: true });
        const applicationConfiguration = await getApplicationConfiguration.call({
            timeout: params?.timeout,
        });
        this.configuration = applicationConfiguration.response;
        this.bootstrappedAt = +(new Date());
        logger.debug(`[EVA:ENDPOINT] Completed bootstrap for ${this.endPointUri}`)();
    }
    /**
     * Returns the current application configuration
     * Will not be populated until bootstrap is called
     *
     * @returns {(EVA.Core.GetApplicationConfigurationResponse|undefined)}
     */
    getApplicationConfiguration() {
        // Return a copy so callers can't modify internal data
        //
        return this.configuration ? JSON.parse(JSON.stringify(this.configuration)) : undefined;
    }
    /**
     * Prepare to call the EVA backend with an instance of the EvaService class
     *
     * @template T
     * @param {new () => T} serviceDefinition
     * @returns EvaService<T>
     */
    prepareService(serviceDefinition) {
        return new EvaService(createServiceDefinition(serviceDefinition), this);
    }
    /**
     * Calls an EVA service on the endpoint
     *
     * @template T
     * @param {new () => T} serviceDefinition
     * @param {T['request']} [payload]
     * @param {IEvaServiceCallOptions} [options]
     * @returns {Promise<T['response']>}
     */
    async callService(serviceDefinition, payload, options) {
        // Build the EVA Service instance using the typed prepare method
        //
        const service = this.prepareService(serviceDefinition);
        // Set the request payload
        //
        service.data.request = payload;
        // Call using provided options
        //
        const result = await service.call(options);
        return result.response;
    }
}
//# sourceMappingURL=endpoint.js.map